import React, { useState, useEffect } from "react";
import { Plus, X, Calendar, TrendingDown, Users, Target } from "lucide-react";
import MainContent from "../MainContent/MainContent";
import { Task } from "../../types/types";
import { realAPI } from "../../services/realAPI";
import { useNotifications } from "../NotificationSystem/NotificationSystem";

interface Sprint {
  id: string;
  name: string;
  description: string;
  startDate: Date;
  endDate: Date;
  status: "planning" | "active" | "completed";
}

interface SprintViewProps {
  sprint: Sprint;
  selectedTeam: any;
  onCreateStory?: (newTask: Task) => void;
  sprintTasks?: Task[];
  onUpdateTask?: (updatedTask: Task) => void;
}

interface NewStory {
  title: string;
  description: string;
  priority: "high" | "medium" | "low";
  storyPoints: string;
  type: "story" | "bug" | "task";
}

const SprintView: React.FC<SprintViewProps> = ({
  sprint,
  selectedTeam,
  onCreateStory,
  sprintTasks,
  onUpdateTask,
}) => {
  const [showCreateStory, setShowCreateStory] = useState(false);
  const [localTasks, setLocalTasks] = useState<Task[]>([]);
  const [isLoadingTasks, setIsLoadingTasks] = useState(true);
  const [isCreatingTask, setIsCreatingTask] = useState(false);
  const [newStory, setNewStory] = useState<NewStory>({
    title: "",
    description: "",
    priority: "medium",
    storyPoints: "",
    type: "story",
  });

  // Attachment-related state
  const [storyAttachments, setStoryAttachments] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<Map<string, string>>(
    new Map()
  );
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);

  const { success, error } = useNotifications();

  // Load tasks for the sprint
  useEffect(() => {
    const loadSprintTasks = async () => {
      try {
        setIsLoadingTasks(true);
        const tasks = await realAPI.getTasksBySprintId(sprint.id);
        setLocalTasks(tasks);
      } catch (error) {
        console.error("Error loading sprint tasks:", error);
        setLocalTasks([]);
      } finally {
        setIsLoadingTasks(false);
      }
    };

    loadSprintTasks();
  }, [sprint.id, sprintTasks]);

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  const getSprintStats = () => {
    const totalStoryPoints = localTasks.reduce(
      (sum, task) => sum + (task.storyPoints || 0),
      0
    );
    const completedStoryPoints = localTasks
      .filter((task) => task.taskColumn === "done")
      .reduce((sum, task) => sum + (task.storyPoints || 0), 0);
    const assignedTasks = localTasks.filter(
      (task) => task.assigneeUserId
    ).length;
    const totalTasks = localTasks.length;

    return {
      totalStoryPoints,
      completedStoryPoints,
      remainingStoryPoints: totalStoryPoints - completedStoryPoints,
      assignedTasks,
      unassignedTasks: totalTasks - assignedTasks,
      totalTasks,
      progressPercentage:
        totalStoryPoints > 0
          ? Math.round((completedStoryPoints / totalStoryPoints) * 100)
          : 0,
    };
  };

  const getSprintStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800";
      case "planning":
        return "bg-blue-100 text-blue-800";
      case "completed":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // File handling functions for story creation
  const isImageFile = (file: File) => {
    return file.type.startsWith("image/");
  };

  const createFilePreview = (file: File): string => {
    if (isImageFile(file)) {
      const fileKey = `${file.name}-${file.size}-${file.lastModified}`;

      // Check if we already have a preview URL for this file
      const existingUrl = previewUrls.get(fileKey);
      if (existingUrl) {
        return existingUrl;
      }

      // Create new URL and store it
      const newUrl = URL.createObjectURL(file);
      setPreviewUrls((prev) => new Map(prev).set(fileKey, newUrl));
      return newUrl;
    }
    return "";
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const fileArray = Array.from(files);

      // Validate file types and sizes
      const validFiles = fileArray.filter((file) => {
        const allowedTypes = [
          "image/jpeg",
          "image/png",
          "image/gif",
          "image/webp",
          "application/pdf",
          "text/plain",
          "application/msword",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        ];

        if (!allowedTypes.includes(file.type)) {
          error(
            "Invalid File Type",
            `${file.name} is not a supported file type.`
          );
          return false;
        }

        if (file.size > 10 * 1024 * 1024) {
          // 10MB limit
          error("File Too Large", `${file.name} is larger than 10MB.`);
          return false;
        }

        return true;
      });

      setStoryAttachments((prev) => [...prev, ...validFiles]);

      // Create preview URLs for new image files
      validFiles.forEach((file) => {
        if (isImageFile(file)) {
          createFilePreview(file);
        }
      });
    }

    // Clear the input
    event.target.value = "";
  };

  const removeAttachment = (index: number) => {
    const fileToRemove = storyAttachments[index];

    // Clean up preview URL to prevent memory leaks
    if (isImageFile(fileToRemove)) {
      const fileKey = `${fileToRemove.name}-${fileToRemove.size}-${fileToRemove.lastModified}`;
      const previewUrl = previewUrls.get(fileKey);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrls((prev) => {
          const newMap = new Map(prev);
          newMap.delete(fileKey);
          return newMap;
        });
      }
    }

    setStoryAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadAttachmentsBeforeCreate = async (): Promise<
    Array<{
      originalFilename: string;
      url: string;
      thumbnailUrl?: string;
    }>
  > => {
    if (storyAttachments.length === 0) return [];

    try {
      setIsUploadingFiles(true);
      const uploadResponse = await realAPI.uploadAttachments(storyAttachments);

      // Handle the API response structure: { success, message, data }
      const attachmentData = uploadResponse.data || uploadResponse;

      // Transform the response to match expected format
      const attachments = attachmentData.map((file: any) => ({
        originalFilename: file.originalFilename || file.filename,
        url: file.url || file.filePath,
        thumbnailUrl: file.thumbnailUrl, // Include thumbnail URL
      }));

      return attachments;
    } catch (error: any) {
      console.error("Error uploading attachments:", error);

      // Handle specific error types with more user-friendly messages
      if (error.details?.isMaxSizeExceeded) {
        throw new Error(
          "One or more files exceed the maximum allowed size. Please choose smaller files or contact your administrator."
        );
      } else if (error.details?.status === 413) {
        throw new Error(
          "Files are too large for the server to process. Please choose smaller files."
        );
      } else if (error.details?.message) {
        throw new Error(`Upload failed: ${error.details.message}`);
      } else {
        throw new Error("Failed to upload attachments. Please try again.");
      }
    } finally {
      setIsUploadingFiles(false);
    }
  };
  const handleCreateStory = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newStory.title.trim() || isCreatingTask) return;

    try {
      setIsCreatingTask(true);

      // Upload attachments first if any
      let attachments: Array<{
        originalFilename: string;
        url: string;
        thumbnailUrl?: string;
      }> = [];

      if (storyAttachments.length > 0) {
        attachments = await uploadAttachmentsBeforeCreate();
      }

      const createdTask = await realAPI.createTask({
        title: newStory.title,
        description: newStory.description,
        priority: newStory.priority,
        taskColumn: "todo",
        status: sprint.id,
        teamId: selectedTeam?.id ?? "",
        assigneeUserId: undefined,
        storyPoints: newStory.storyPoints
          ? parseInt(newStory.storyPoints)
          : undefined,
        type: newStory.type,
        attachments: attachments.length > 0 ? attachments : undefined,
      });

      setLocalTasks((prevTasks) => [...prevTasks, createdTask]);

      if (onCreateStory) {
        onCreateStory(createdTask);
      }

      // Success notification with attachment info
      success(
        "Story Created",
        `"${createdTask.title}" has been added to ${sprint.name}.${
          attachments.length > 0
            ? ` ${attachments.length} file(s) attached.`
            : ""
        }`
      );

      // Reset form and attachments
      // Clean up preview URLs
      storyAttachments.forEach((file) => {
        if (isImageFile(file)) {
          const fileKey = `${file.name}-${file.size}-${file.lastModified}`;
          const previewUrl = previewUrls.get(fileKey);
          if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
          }
        }
      });

      setNewStory({
        title: "",
        description: "",
        priority: "medium",
        storyPoints: "",
        type: "story",
      });
      setStoryAttachments([]);
      setPreviewUrls(new Map());
      setShowCreateStory(false);
    } catch (err) {
      console.error("Error creating story:", err);
      const message = (err as Error)?.message || String(err);
      if (/insufficient balance for stories/i.test(message)) {
        error(
          "Insufficient Stories Balance",
          "No remaining story credits. Please top up your STORIES quota in Quotas & Balance."
        );
      } else {
        error("Failed to create story", message);
      }
    } finally {
      setIsCreatingTask(false);
    }
  };

  const handleCancelCreate = () => {
    if (isCreatingTask) return;

    // Clean up preview URLs
    storyAttachments.forEach((file) => {
      if (isImageFile(file)) {
        const fileKey = `${file.name}-${file.size}-${file.lastModified}`;
        const previewUrl = previewUrls.get(fileKey);
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
        }
      }
    });

    setNewStory({
      title: "",
      description: "",
      priority: "medium",
      storyPoints: "",
      type: "story",
    });
    setStoryAttachments([]);
    setPreviewUrls(new Map());
    setShowCreateStory(false);
  };

  const stats = getSprintStats();

  // Update a task locally and persist column changes; bubble to parent if provided
  const handleUpdateTaskInternal = async (updatedTask: Task) => {
    setLocalTasks((prev) => {
      const prevTask = prev.find((t) => t.id === updatedTask.id);
      const next = prev.map((t) =>
        t.id === updatedTask.id ? { ...t, ...updatedTask } : t
      );

      // Persist column change to backend if it changed
      if (
        prevTask &&
        updatedTask.taskColumn &&
        prevTask.taskColumn !== updatedTask.taskColumn
      ) {
        realAPI
          .updateTaskColumn(String(updatedTask.id), updatedTask.taskColumn)
          .catch((e) =>
            console.error("Failed to update task column in backend:", e)
          );
      }

      return next;
    });

    // Propagate to parent if consumer passed a handler
    if (onUpdateTask) {
      try {
        onUpdateTask(updatedTask);
      } catch (e) {
        // No-op if parent handler throws; local state already updated
      }
    }
  };

  if (isLoadingTasks) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Sprint Header with Create Story Button */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {sprint.name}
            </h2>
            <p className="text-sm text-gray-600">{sprint.description}</p>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setShowCreateStory(true)}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
              disabled={isCreatingTask}
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Story
            </button>
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${getSprintStatusColor(
                sprint.status
              )}`}
            >
              {sprint.status.charAt(0).toUpperCase() + sprint.status.slice(1)}
            </span>
            <span className="text-sm text-gray-500">
              {formatDate(sprint.startDate)} - {formatDate(sprint.endDate)}
            </span>
          </div>
        </div>

        {/* Sprint Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center">
              <TrendingDown className="h-8 w-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">
                  Story Points
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.completedStoryPoints}/{stats.totalStoryPoints}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center">
              <Target className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Progress</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.progressPercentage}%
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center">
              <Calendar className="h-8 w-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Tasks</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.totalTasks}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center">
              <Users className="h-8 w-8 text-orange-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Assigned</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.assignedTasks}/{stats.totalTasks}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="bg-white rounded-lg border p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              Sprint Progress
            </span>
            <span className="text-sm text-gray-500">
              {stats.progressPercentage}% Complete
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${stats.progressPercentage}%` }}
            ></div>
          </div>
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>{stats.completedStoryPoints} points completed</span>
            <span>{stats.remainingStoryPoints} points remaining</span>
          </div>
        </div>
      </div>

      {/* Create Story Modal */}
      {showCreateStory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl my-8 max-h-[calc(100vh-2rem)] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Create Story for {sprint.name}
              </h2>
              <button
                onClick={handleCancelCreate}
                className="text-gray-400 hover:text-gray-600"
                disabled={isCreatingTask}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateStory}>
              <div className="p-6 space-y-4">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={newStory.title}
                    onChange={(e) =>
                      setNewStory((prev) => ({
                        ...prev,
                        title: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter story title..."
                    autoFocus
                    disabled={isCreatingTask}
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={newStory.description}
                    onChange={(e) =>
                      setNewStory((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter story description..."
                    disabled={isCreatingTask}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Type
                    </label>
                    <select
                      value={newStory.type}
                      onChange={(e) =>
                        setNewStory((prev) => ({
                          ...prev,
                          type: e.target.value as "story" | "bug" | "task",
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={isCreatingTask}
                    >
                      <option value="story">📖 Story</option>
                      <option value="bug">🐛 Bug</option>
                      <option value="task">✅ Task</option>
                    </select>
                  </div>

                  {/* Priority */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Priority
                    </label>
                    <select
                      value={newStory.priority}
                      onChange={(e) =>
                        setNewStory((prev) => ({
                          ...prev,
                          priority: e.target.value as "high" | "medium" | "low",
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={isCreatingTask}
                    >
                      <option value="high">🔴 High</option>
                      <option value="medium">🟡 Medium</option>
                      <option value="low">🟢 Low</option>
                    </select>
                  </div>

                  {/* Story Points */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Story Points
                    </label>
                    <input
                      type="number"
                      value={newStory.storyPoints}
                      onChange={(e) =>
                        setNewStory((prev) => ({
                          ...prev,
                          storyPoints: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Points"
                      min="1"
                      max="100"
                      disabled={isCreatingTask}
                    />
                  </div>
                </div>

                {/* Sprint Assignment Info */}
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium text-blue-900">
                      This story will be added to: {sprint.name}
                    </span>
                  </div>
                  <p className="text-xs text-blue-700 mt-1">
                    You can move it to backlog or other sprints later using drag
                    & drop
                  </p>
                </div>

                {/* Attachments Section */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Attachments (Optional)
                  </label>

                  {/* File Upload Button */}
                  <div className="mb-3">
                    <label className="inline-flex items-center px-4 py-2 bg-purple-100 text-purple-700 rounded-lg border border-purple-300 cursor-pointer hover:bg-purple-200 transition-colors">
                      <svg
                        className="w-4 h-4 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                        />
                      </svg>
                      Choose Files
                      <input
                        type="file"
                        multiple
                        onChange={handleFileSelect}
                        accept="image/*,.pdf,.doc,.docx,.txt"
                        className="hidden"
                        disabled={isCreatingTask || isUploadingFiles}
                      />
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                      Images, PDFs, and documents up to 10MB each
                    </p>
                  </div>

                  {/* Selected Files List */}
                  {storyAttachments.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-3">
                      {storyAttachments.map((file, index) => (
                        <div
                          key={index}
                          className="relative bg-gray-50 p-4 rounded border flex flex-col items-center"
                        >
                          {/* Remove button - positioned at top right */}
                          <button
                            onClick={() => removeAttachment(index)}
                            disabled={isCreatingTask || isUploadingFiles}
                            className="absolute top-2 right-2 text-red-500 hover:text-red-700 disabled:text-gray-400 p-1 bg-white rounded-full shadow-sm"
                          >
                            <X className="w-4 h-4" />
                          </button>

                          {/* File thumbnail or icon */}
                          {isImageFile(file) ? (
                            <img
                              src={createFilePreview(file)}
                              alt={file.name}
                              className="w-48 h-48 rounded object-cover border mb-3"
                            />
                          ) : (
                            <div className="w-48 h-48 bg-gray-200 rounded flex items-center justify-center mb-3">
                              <svg
                                className="w-20 h-20 text-gray-500"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                                />
                              </svg>
                            </div>
                          )}

                          {/* File info */}
                          <div className="text-center w-full px-2">
                            <span
                              className="text-sm text-gray-700 block truncate mb-1"
                              title={file.name}
                            >
                              {file.name}
                            </span>
                            <span className="text-xs text-gray-500">
                              {(file.size / 1024).toFixed(1)}KB
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Upload Progress */}
                  {isUploadingFiles && (
                    <div className="flex items-center space-x-2 text-blue-600 mb-3">
                      <svg
                        className="animate-spin w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      <span className="text-sm">Uploading files...</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleCancelCreate}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                  disabled={isCreatingTask || isUploadingFiles}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={
                    !newStory.title.trim() || isCreatingTask || isUploadingFiles
                  }
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                >
                  {(isCreatingTask || isUploadingFiles) && (
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  )}
                  <span>
                    {isUploadingFiles
                      ? "Uploading..."
                      : isCreatingTask
                      ? "Creating..."
                      : "Create Story"}
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Sprint Kanban Board */}
      <MainContent
        key={localTasks.length}
        initialTasks={localTasks}
        onUpdateTask={handleUpdateTaskInternal}
      />
    </div>
  );
};

export default SprintView;
